/**
 * Finding the starting point: one branch at a time, a handful of plain
 * questions each (binary search anchored on the child's age). The parent
 * answers first; a therapist can run it again later on their branches.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { api } from '@/core/api';
import { fa } from '@/core/jalali';
import { useProfile } from '@/app/ProfileShell';

interface Q {
  branch: string;
  branchTitle: string;
  nodeKey: string;
  nodeLabel: string;
  nodeTitle: string;
  questionIndex: number;
  text: string;
  achievedWhen: string;
  emergingWhen: string | null;
  asked: number;
  remainingMax: number;
}
interface Br {
  branch: string;
  title: string;
  order: number;
  stepCount: number;
  askable: number;
  asked: number;
  done: boolean;
  anchorNode: string | null;
  anchorTitle: string | null;
  nextNode: string | null;
  nextTitle: string | null;
}
type Answer = 'yes' | 'sometimes' | 'no' | 'dont_know';

const ANSWERS: { v: Answer; t: string; cls: string }[] = [
  { v: 'yes', t: 'بله', cls: 'primary' },
  { v: 'sometimes', t: 'گاهی / با کمک', cls: 'quiet' },
  { v: 'no', t: 'هنوز نه', cls: 'quiet' },
  { v: 'dont_know', t: 'نمی‌دانم', cls: 'quiet' },
];

export function BaselinePage() {
  const { id } = useParams({ from: '/authed/p/$id' });
  const profile = useProfile();
  const qc = useQueryClient();
  const [branch, setBranch] = useState<string | null>(null);
  const [current, setCurrent] = useState<Q | null>(null);
  const [finished, setFinished] = useState<Br | null>(null);

  const overview = useQuery({
    queryKey: ['baseline', id],
    queryFn: () => api<Br[]>({ path: `/v1/profiles/${id}/baseline` }),
  });

  const start = useMutation({
    mutationFn: (b: string) =>
      api<{ question: Q | null; branch: Br }>({ path: `/v1/profiles/${id}/baseline/${b}/next` }),
    onSuccess: (r, b) => {
      setBranch(b);
      setCurrent(r.question);
      setFinished(r.question ? null : r.branch);
    },
  });

  const answer = useMutation({
    mutationFn: (a: Answer) =>
      api<{ next: Q | null; branch: Br }>({
        method: 'POST',
        path: `/v1/profiles/${id}/baseline/${branch}/answer`,
        body: {
          nodeKey: current!.nodeKey,
          questionIndex: current!.questionIndex,
          answer: a,
          clientEventId: `web-${crypto.randomUUID()}`,
        },
      }),
    onSuccess: (r) => {
      setCurrent(r.next);
      if (!r.next) setFinished(r.branch);
      void qc.invalidateQueries({ queryKey: ['baseline', id] });
      void qc.invalidateQueries({ queryKey: ['profile', id] });
      void qc.invalidateQueries({ queryKey: ['state', id] });
      void qc.invalidateQueries({ queryKey: ['today', id] });
    },
  });

  const open = (overview.data ?? []).filter((b) => b.askable > 0);
  const remaining = open.filter((b) => !b.done).length;

  if (branch && current) {
    return (
      <main className="page">
        <div className="row spread">
          <span className="overline">{current.branchTitle}</span>
          <span className="help num">
            سؤال {fa(current.asked + 1)} · حداکثر {fa(current.asked + current.remainingMax)}
          </span>
        </div>
        <section className="card">
          <div className="help">
            {current.nodeLabel} · {current.nodeTitle}
          </div>
          <h2 style={{ fontSize: 'var(--fs-section)', fontWeight: 700 }}>{current.text}</h2>
          {(current.achievedWhen !== 'بله' || current.emergingWhen) && (
            <p className="help">
              {current.achievedWhen !== 'بله' ? `«بله» یعنی: ${current.achievedWhen}` : ''}
              {current.emergingWhen
                ? `${current.achievedWhen !== 'بله' ? ' · ' : ''}«گاهی» یعنی: ${current.emergingWhen}`
                : ''}
            </p>
          )}
          <div className="row">
            {ANSWERS.map((a) => (
              <button
                key={a.v}
                type="button"
                className={`btn ${a.cls}`}
                disabled={answer.isPending}
                onClick={() => answer.mutate(a.v)}
              >
                {a.t}
              </button>
            ))}
          </div>
        </section>
        <button type="button" className="btn quiet block" onClick={() => setBranch(null)}>
          بعداً ادامه می‌دهم
        </button>
      </main>
    );
  }

  return (
    <main className="page">
      <h2 className="page-title">نقطهٔ شروع {profile.displayName}</h2>
      <p className="help">
        برای هر بخش چند سؤال ساده می‌پرسیم و از همان جایی شروع می‌کنیم که {profile.displayName} الان
        هست، نه از اول. سؤال‌ها با سن او تنظیم شده‌اند.
      </p>

      {finished && (
        <section
          className="card"
          style={{ background: 'var(--st-achieved-bg)', borderColor: 'var(--st-achieved-ring)' }}
        >
          <div className="overline" style={{ color: 'var(--st-achieved)' }}>
            {finished.title}: تمام شد
          </div>
          <p>
            {finished.anchorTitle ? (
              <>
                نقطهٔ شروع: <strong>{finished.anchorTitle}</strong>
              </>
            ) : (
              'از اولین پله شروع می‌کنیم.'
            )}
            {finished.nextTitle ? ` · قدم بعدی: ${finished.nextTitle}` : ''}
          </p>
          <Link
            to="/p/$id/path/$branch"
            params={{ id, branch: finished.branch }}
            className="btn quiet"
          >
            دیدن مسیر
          </Link>
        </section>
      )}

      {overview.isLoading && <div className="skeleton" />}
      {remaining === 0 && overview.data && (
        <section className="card hero">
          <strong>همهٔ بخش‌ها نقطهٔ شروع دارند.</strong>
          <span className="help">
            هر وقت خواستید می‌توانید یک بخش را دوباره بپرسید؛ درمانگر هم می‌تواند.
          </span>
        </section>
      )}

      {open.map((b) => (
        <section key={b.branch} className="card">
          <div className="row spread">
            <strong>{b.title}</strong>
            <span className={`chip ${b.done ? 'achieved' : b.asked > 0 ? 'emerging' : 'unknown'}`}>
              {b.done ? 'انجام شد' : b.asked > 0 ? 'نیمه‌کاره' : 'شروع نشده'}
            </span>
          </div>
          <div className="help">
            {b.done && b.anchorTitle
              ? `نقطهٔ شروع: ${b.anchorTitle}`
              : b.done
                ? 'از اولین پله'
                : `${fa(b.askable)} پله · حدود ${fa(Math.ceil(Math.log2(b.askable + 1)))} سؤال`}
          </div>
          <button
            type="button"
            className={`btn ${b.done ? 'quiet' : 'primary'}`}
            disabled={start.isPending}
            onClick={() => start.mutate(b.branch)}
          >
            {b.done ? 'دوباره بپرس' : b.asked > 0 ? 'ادامه' : 'شروع'}
          </button>
        </section>
      ))}
    </main>
  );
}
